import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dropdown, MenuGroup, MenuItem, cn, useToast } from "@vertesia/ui/core";
import { useUserSession } from "@vertesia/ui/session";
import { Bot, ClipboardList, CopyIcon, DownloadCloudIcon, ExternalLink, GitFork, InfoIcon, MoreVertical, RefreshCcw, XIcon } from "lucide-react";
import { useUITranslation } from '../../../../i18n/index.js';
import { PayloadBuilderProvider, usePayloadBuilder } from "../../PayloadBuilder";
import { getConversationUrl } from "./utils";
export default function Header({ title, isTerminal = false, onClose, isModal, agentRunId, workflowRunId, viewMode, onViewModeChange, showPlanPanel, hasPlan = false, showPlanButton = true, onTogglePlanPanel, onDownload, 
// onCopyRunId,
resetWorkflow, onExportPdf, onShowDetails, onRestart, onClone, isReceivingChunks = false, className, }) {
    const { t } = useUITranslation();
    const continueWorkflow = useContinueWorkflow(agentRunId, onRestart);
    return (_jsx(PayloadBuilderProvider, { children: _jsxs("div", { className: cn("flex flex-wrap items-end justify-between py-1.5 px-2 border-b shadow-sm flex-shrink-0", className), children: [_jsxs("div", { className: "flex flex-wrap items-center space-x-2", children: [_jsxs("div", { className: "flex items-center space-x-1", children: [_jsx(Bot, { className: "size-5 text-muted" }), _jsx("span", { className: "font-medium", children: title })] }), _jsxs("span", { className: "text-xs text-muted ml-1 flex items-center gap-1.5", children: ["(Agent Run ID: ", agentRunId, ")", _jsx("span", { className: cn("w-2 h-2 rounded-full transition-colors duration-200", isReceivingChunks
                                        ? "bg-purple-500 shadow-[0_0_6px_2px_rgba(168,85,247,0.6)]"
                                        : "bg-gray-400") })] })] }), _jsxs("div", { className: "flex justify-end items-center space-x-2 ml-auto", children: [_jsxs("div", { className: "flex items-center space-x-1 bg-muted rounded p-0.5 mt-2 lg:mt-0", children: [_jsx(Button, { variant: viewMode === "stacked" ? "outline" : "ghost", size: "xs", onClick: () => onViewModeChange("stacked"), children: t('agent.details') }), _jsx(Button, { variant: viewMode === "sliding" ? "outline" : "ghost", size: "xs", onClick: () => onViewModeChange("sliding"), children: t('agent.summary') })] }), showPlanButton && (_jsxs("div", { className: "relative", children: [hasPlan && !showPlanPanel && (_jsx("span", { className: "absolute -top-1 -right-1 w-2.5 h-2.5 bg-primary rounded-full border border-border z-10" })), _jsxs(Button, { size: "sm", variant: showPlanPanel ? "primary" : "secondary", onClick: onTogglePlanPanel, className: "transition-all duration-200 rounded-md", title: t('agent.toggleRightSidebar'), children: [_jsx(ClipboardList, { className: "size-4 mr-1.5" }), _jsx("span", { className: "font-medium text-xs", children: showPlanPanel ? t('agent.hideSidebar') : t('agent.showSidebar') })] })] })), onRestart && isTerminal && (_jsxs(Button, { size: "sm", variant: "secondary", onClick: continueWorkflow, className: "transition-all duration-200 rounded-md", title: t('agent.continueConversation'), children: [_jsx(RefreshCcw, { className: "size-4 mr-1.5" }), _jsx("span", { className: "font-medium text-xs", children: t('agent.continueConversation') })] })), _jsx(MoreDropdown, { agentRunId: agentRunId, workflowRunId: workflowRunId, isModal: isModal, isTerminal: isTerminal, onClose: onClose, onDownload: onDownload, resetWorkflow: resetWorkflow, onExportPdf: onExportPdf, onShowDetails: onShowDetails, onRestart: onRestart, onClone: onClone }), onClose && !isModal && (_jsx(Button, { size: "xs", variant: "ghost", onClick: onClose, children: _jsx(XIcon, { className: "size-4" }) }))] })] }) }));
}
function useContinueWorkflow(agentRunId, onRestart) {
    const { t } = useUITranslation();
    const toast = useToast();
    const { client } = useUserSession();
    return async () => {
        try {
            const newRun = await client.agents.restart(agentRunId);
            toast({
                status: "success",
                title: t('agent.conversationContinued'),
                duration: 2000,
            });
            onRestart?.(newRun);
        }
        catch (_error) {
            toast({
                status: "error",
                title: t('agent.failedToContinueConversation'),
                duration: 2000,
            });
        }
    };
}
function MoreDropdown({ agentRunId, workflowRunId, isModal, isTerminal, onClose, onDownload, resetWorkflow, onExportPdf, onShowDetails, onRestart, onClone, }) {
    const { t } = useUITranslation();
    const toast = useToast();
    const { client } = useUserSession();
    const builder = usePayloadBuilder();
    const continueWorkflow = useContinueWorkflow(agentRunId, onRestart);
    const cancelWorkflow = async () => {
        try {
            await client.agents.terminate(agentRunId, "cancel");
            toast({
                status: "success",
                title: t('agent.workflowCancelled'),
                duration: 2000,
            });
            builder.reset();
            resetWorkflow?.();
            return true;
        }
        catch (_error) {
            toast({
                status: "error",
                title: t('agent.failedToCancelWorkflow'),
                duration: 2000,
            });
            return false;
        }
    };
    const cloneWorkflow = async () => {
        try {
            const newRun = await client.agents.fork(agentRunId);
            toast({
                status: "success",
                title: t('agent.conversationCloned'),
                duration: 2000,
            });
            onClone?.(newRun);
        }
        catch (_error) {
            toast({
                status: "error",
                title: t('agent.failedToCloneConversation'),
                duration: 2000,
            });
        }
    };
    const openUrl = (url) => {
        window.open(url, "_blank");
        return url;
    };
    const copyAgentRunId = () => {
        navigator.clipboard.writeText(agentRunId);
        toast({
            status: "success",
            title: t('agent.agentRunIdCopied'),
            duration: 2000,
        });
    };
    const copyWorkflowRunId = () => {
        navigator.clipboard.writeText(workflowRunId);
        toast({
            status: "success",
            title: t('agent.workflowRunIdCopied'),
            duration: 2000,
        });
    };
    return (_jsx(Dropdown, { align: "right", trigger: _jsx(Button, { size: "xs", variant: "ghost", title: t('agent.moreActions'), children: _jsx(MoreVertical, { className: "size-4" }) }), children: _jsxs(MenuGroup, { label: "Actions", children: [isModal && (_jsxs(MenuItem, { onClick: () => openUrl(`/store/agent-runner/${agentRunId}`), children: [_jsx(ExternalLink, { className: "size-3.5 text-muted" }), " ", t('agent.openInNewTab')] })), _jsxs(MenuItem, { onClick: copyAgentRunId, children: [_jsx(CopyIcon, { className: "size-3.5 text-muted" }), " ", t('agent.copyAgentRunId')] }), _jsxs(MenuItem, { onClick: copyWorkflowRunId, children: [_jsx(CopyIcon, { className: "size-3.5 text-muted" }), " ", t('agent.copyWorkflowRunId')] }), onShowDetails && (_jsxs(MenuItem, { onClick: onShowDetails, children: [_jsx(InfoIcon, { className: "size-3.5 text-muted" }), " ", t('agent.details')] })), _jsxs(MenuItem, { onClick: () => {
                        if (onDownload) {
                            onDownload();
                        }
                        else {
                            getConversationUrl(client, agentRunId).then((r) => window.open(r, "_blank"));
                        }
                    }, children: [_jsx(DownloadCloudIcon, { className: "size-3.5 text-muted" }), " ", t('agent.downloadConversation')] }), onExportPdf && (_jsxs(MenuItem, { onClick: onExportPdf, children: [_jsx(DownloadCloudIcon, { className: "size-3.5 text-muted" }), " ", t('agent.exportAsPdf')] })), onClose && isModal && (_jsxs(MenuItem, { onClick: onClose, children: [_jsx(XIcon, { className: "size-3.5 text-muted" }), " ", t('agent.close')] })), onRestart && isTerminal && (_jsxs(MenuItem, { onClick: continueWorkflow, children: [_jsx(RefreshCcw, { className: "size-3.5 text-muted" }), " ", t('agent.continueConversation')] })), onClone && (_jsxs(MenuItem, { onClick: cloneWorkflow, children: [_jsx(GitFork, { className: "size-3.5 text-muted" }), " ", t('agent.cloneConversation')] })), !isTerminal && (_jsxs(MenuItem, { onClick: cancelWorkflow, variant: "destructive", children: [_jsx(XIcon, { className: "size-3.5" }), " ", t('agent.cancelWorkflow')] }))] }) }));
}
//# sourceMappingURL=Header.js.map